import { resolveMediaQuery } from './mapMediaQueries';

const BLOCK_SELECTOR_RE = /#lp-pom-block-(\d+)/;

function normalizeUrl(url) {
  if (!url) return url;
  if (url.startsWith('//')) return `https:${url}`;
  return url;
}

function extractBgUrl(declarationBlock) {
  const bgImageRe = /background-image:\s*url\(\s*(['"]?)(.*?)\1\s*\)/gi;
  let lastUrl = null;
  let match;
  while ((match = bgImageRe.exec(declarationBlock)) !== null) {
    const raw = match[2].trim();
    if (raw && raw !== 'initial' && raw !== 'none') {
      lastUrl = raw;
    }
  }
  return lastUrl;
}

function processRule(ruleText, mediaQuery, blocks) {
  const ruleRe = /([^{}]+)\{([^}]*)\}/g;
  let match;
  while ((match = ruleRe.exec(ruleText)) !== null) {
    const selector = match[1].trim();
    const body = match[2];
    const blockMatch = selector.match(BLOCK_SELECTOR_RE);
    if (!blockMatch) continue;

    const blockId = `#lp-pom-block-${blockMatch[1]}`;
    const url = extractBgUrl(body);
    if (!url) continue;

    if (!blocks.has(blockId)) blocks.set(blockId, new Map());
    blocks.get(blockId).set(mediaQuery, normalizeUrl(url));
  }
}

function extractAllBlockImages(cssTexts) {
  const blocks = new Map();

  for (const cssText of cssTexts) {
    // Extract rules inside @media blocks
    let mediaCleaned = cssText;
    const mediaBlockRe = /@media\s+([\s\S]*?)\{\s*([\s\S]*?\{[\s\S]*?\})\s*\}/g;
    let mediaMatch;
    while ((mediaMatch = mediaBlockRe.exec(cssText)) !== null) {
      const mediaQuery = mediaMatch[1].trim();
      const innerRules = mediaMatch[2];
      processRule(innerRules, mediaQuery, blocks);
      mediaCleaned = mediaCleaned.replace(mediaMatch[0], '');
    }

    // Extract top-level rules (no @media wrapper = desktop standard)
    processRule(mediaCleaned, null, blocks);
  }

  return blocks;
}

// Heuristic: the hero block is the one with the most responsive image variants
// (i.e. the most @media query entries), with lowest block number as tiebreaker.
// This works well for typical Unbounce pages but can misfire on unusual layouts —
// callers can pass an explicit heroBlockId to override auto-detection.
function findHeroBlockId(blocks, heroBlockId) {
  if (heroBlockId && blocks.has(heroBlockId)) return heroBlockId;

  let bestId = null;
  let bestCount = 0;
  let bestNum = Infinity;

  for (const [blockId, variants] of blocks) {
    const count = variants.size;
    const num = parseInt(blockId.replace('#lp-pom-block-', ''), 10);
    if (count > bestCount || (count === bestCount && num < bestNum)) {
      bestId = blockId;
      bestCount = count;
      bestNum = num;
    }
  }

  return bestId;
}

// --- Resolution scoping ---

function extractDPR(rawMediaQuery) {
  if (!rawMediaQuery) return 0;
  const match = rawMediaQuery.match(/-device-pixel-ratio:\s*([\d.]+)/i);
  return match ? parseFloat(match[1]) : 0;
}

function extractDPI(rawMediaQuery) {
  if (!rawMediaQuery) return 0;
  const match = rawMediaQuery.match(/min-resolution:\s*(\d+)\s*dpi/i);
  return match ? parseInt(match[1], 10) : 0;
}

function hasResolutionCondition(rawMediaQuery) {
  if (!rawMediaQuery) return false;
  return /device-pixel-ratio|min-resolution/i.test(rawMediaQuery);
}

function isMobileQuery(rawMediaQuery) {
  if (!rawMediaQuery) return false;
  return /max-width/i.test(rawMediaQuery);
}

function appendUpperBounds(media, isStandard, maxDPR, maxDPI) {
  const parts = media.split(',').map((s) => s.trim());

  if (isStandard) {
    const augmented = [];
    if (maxDPR !== null) {
      for (const part of parts)
        augmented.push(`${part} and (-webkit-max-device-pixel-ratio: ${maxDPR})`);
    }
    if (maxDPI !== null) {
      for (const part of parts)
        augmented.push(`${part} and (max-resolution: ${maxDPI}dpi)`);
    }
    return augmented.join(', ');
  }

  return parts
    .map((part) => {
      if (/-webkit-min-device-pixel-ratio/i.test(part) && maxDPR !== null)
        return `${part} and (-webkit-max-device-pixel-ratio: ${maxDPR})`;
      if (/min-resolution/i.test(part) && maxDPI !== null)
        return `${part} and (max-resolution: ${maxDPI}dpi)`;
      return part;
    })
    .join(', ');
}

function scopePlatform(preloads, mobile) {
  const variants = preloads.filter(
    (p) => isMobileQuery(p.rawMediaQuery) === mobile
  );
  if (variants.length <= 1) return;

  const sorted = [...variants].sort(
    (a, b) => extractDPR(a.rawMediaQuery) - extractDPR(b.rawMediaQuery)
  );

  for (let i = 0; i < sorted.length - 1; i++) {
    const current = sorted[i];
    const next = sorted[i + 1];
    const nextDPR = extractDPR(next.rawMediaQuery);
    const nextDPI = extractDPI(next.rawMediaQuery);
    const maxDPR = nextDPR > 0 ? (nextDPR - 0.01).toFixed(2) : null;
    const maxDPI = nextDPI > 0 ? nextDPI - 1 : null;

    if (maxDPR === null && maxDPI === null) continue;

    const isStandard = !hasResolutionCondition(current.rawMediaQuery);
    current.media = appendUpperBounds(current.media, isStandard, maxDPR, maxDPI);
  }
}

function scopeResolutionRanges(preloads) {
  scopePlatform(preloads, true);
  scopePlatform(preloads, false);
}

// --- Main entry point ---

export function extractPreloads(cssTexts, heroBlockId = null) {
  const blocks = extractAllBlockImages(cssTexts);
  if (blocks.size === 0) return { error: 'no-hero-images', preloads: [] };

  const heroId = findHeroBlockId(blocks, heroBlockId);
  if (!heroId) return { error: 'no-hero-images', preloads: [] };

  const variants = blocks.get(heroId);
  const preloads = [];

  for (const [rawMediaQuery, url] of variants) {
    const resolved = resolveMediaQuery(rawMediaQuery);
    preloads.push({
      category: resolved.label,
      url,
      media: resolved.media,
      sortOrder: resolved.sortOrder,
      rawMediaQuery,
    });
  }

  scopeResolutionRanges(preloads);
  preloads.sort((a, b) => a.sortOrder - b.sortOrder);

  if (preloads.length === 0) return { error: 'no-hero-images', preloads: [] };

  return { error: null, preloads, heroBlockId: heroId };
}

export function formatPreloadTags(preloads) {
  return preloads
    .map(({ category, url, media }) => {
      const comment = `<!-- ${category} Hero Preload -->`;
      const mediaAttr = media ? ` media="${media}"` : '';
      const link = `<link rel="preload" as="image" href="${url}" fetchpriority="high"${mediaAttr}>`;
      return `${comment}${link}`;
    })
    .join('\n');
}
